import { getModule as getStoreModule } from 'vuex-module-decorators';
import GlobalsStoreModule from '@app/store/modules/GlobalsStoreModule';
import IConnectionNavigationItem from '@app/types/data/base/IConnectionNavigationItem';
import { colors } from '@app/services/export/ppt/variables/constants';
import PptxGenJS from 'pptxgenjs';
import { Connection } from '@app/services/export/ppt/classes/Connection';
import { TextWithDimensions } from '@app/services/export/ppt/classes/TextWithDimensions';
import { copy, copyMini } from '@app/services/export/ppt/variables/typography';
import global from '@app/filters/global';
import { AbstractPptComponent, IPptComponentPosition } from '@app/services/export/ppt/classes/AbstractPptComponent';

interface IProps {
	list: IConnectionNavigationItem[];
	current?: number;
	navigationStartOffset?: number;
}

export class ConnectionNavigation extends AbstractPptComponent {
	protected props: IProps;

	protected slide: PptxGenJS.Slide;

	protected readonly i18n = getStoreModule(GlobalsStoreModule).i18n;

	public static readonly itemHeight = 0.65;

	public static readonly itemGap = 0.06;

	public constructor(slide: PptxGenJS.Slide, props: IProps, position: IPptComponentPosition) {
		super(position);
		this.slide = slide;
		this.props = props;
	}

	public create(): void {
		this.props.list.forEach((item, index) => {
			const itemY =
				index === 0
					? this.position.y
					: this.position.y + (ConnectionNavigation.itemHeight + ConnectionNavigation.itemGap) * index;
			const priceWidth = 0.65;
			const itemPaddingH = 0.1;
			const {connection} = item;
			const maxWidth = this.position.w - 2 * itemPaddingH;
			const navigationNumber = index + (this.props.navigationStartOffset || 0);
			let subText = null;

			const connectionText = new Connection(this.slide, {
				connection,
				maxWidth: item.price ? maxWidth - priceWidth + 0.05 : maxWidth
			});

			if (connection.connectingTicketInfo) {
				subText = new TextWithDimensions({
					text: connection.connectingTicketInfo,
					maxWidth: item.price ? maxWidth - (priceWidth + 0.1) : maxWidth - 0.1,
					hBuffer: 0.07,
					textOptions: {
						...copyMini,
						color: colors.blue
					}
				});
			}

			// background color
			this.slide.addShape('rect', {
				x: this.position.x,
				y: itemY,
				w: this.position.w,
				h: ConnectionNavigation.itemHeight,
				fill: { color: index + 1 === this.props.current ? colors.lightYellow : colors.lightGray }
			});

			const subTextHeight = subText ? subText.getDimensions().h : 0;
			const connectionTextHeight = connectionText.getDimensions().h;
			const totalHeight = subTextHeight + connectionTextHeight;

			// create text
			connectionText.createAtCoordinates(
				this.position.x + itemPaddingH,
				itemY + ConnectionNavigation.itemHeight / 2 - totalHeight / 2
			);

			if (subText) {
				subText.createForSlide(this.slide, {
					x: this.position.x + itemPaddingH,
					y: itemY + ConnectionNavigation.itemHeight / 2 - totalHeight / 2 + connectionTextHeight
				});
			}

			// price
			if (item.price) {
				this.slide.addText(
					[
						{ text: global.formatPrice(item.price), options: { ...copy, breakLine: true } },
						{ text: this.i18n.perPerson, options: { ...copyMini } }
					],
					{
						color: colors.blue,
						x: this.position.x + this.position.w - priceWidth,
						y: itemY,
						w: priceWidth,
						h: ConnectionNavigation.itemHeight,
						fill: { color: index + 1 === this.props.current ? colors.yellow : colors.gray }
					}
				);
			}

			// link
			if (navigationNumber) {
				this.slide.addShape('rect', {
					x: this.position.x,
					y: itemY,
					w: this.position.w,
					h: ConnectionNavigation.itemHeight,
					hyperlink: { slide: navigationNumber }
				});
			}
		});
	}

	protected getHeight(): number {
		return this.props.list.length * (ConnectionNavigation.itemHeight + ConnectionNavigation.itemGap);
	}
}
